import { format } from 'date-fns';
import { TFunction } from 'i18next';
import { FieldType } from '90.quickConnect.Models/enums';
import { isAnArrayOfChoice } from '90.quickConnect.Models/guards';
import {
  AllFieldValueTypes,
  AttachmentItemData,
  Choice,
  FieldDesc,
  GeolocationData,
  QCNotification,
} from '90.quickConnect.Models/models';

const cleanLabel = (label: string): string =>
  label
    .replace(/(<([^>]+)>)/gi, '')
    .replace(/&nbsp;/gi, '')
    .trim();

const formatDateValue = (value: AllFieldValueTypes, datePattern: string): string => {
  if (!value) return '';
  const dateValue = value instanceof Date ? value : new Date(value as string);
  if (Number.isNaN(dateValue.getTime())) return '';
  return format(dateValue, datePattern);
};

const getChoiceListLabel = (value: AllFieldValueTypes): string => {
  if (!isAnArrayOfChoice(value)) return '';
  return value
    .map(({ label }: Choice) => label)
    .filter(Boolean)
    .join(', ');
};

const getNotificationLabel = (value: AllFieldValueTypes, t: TFunction): string => {
  const notification = value as QCNotification;
  if (!notification) return '';

  const targets = notification.selectedTargets?.map((item) => item.target).filter(Boolean) ?? [];
  let summary = targets.join(', ');
  if (notification.sendToMe) {
    summary = summary ? `${summary}, ${t('formlib_notification_send_to_me')}` : t('formlib_notification_send_to_me');
  }

  return summary;
};

/**
 * Compute a formatted summary label for a field based on its type and value.
 * Handles field-specific formatting (dates, attachments, notifications, etc.).
 *
 * @param field - The field descriptor
 * @param t - i18next translation function
 * @returns Formatted label string, or empty string if the field should not be displayed
 */
const getSummaryLabel = (field: FieldDesc, t: TFunction): string => {
  const normalizedLabel = cleanLabel(field.label ?? '');
  if (!normalizedLabel) return '';

  const { fieldType, value, type } = field;

  switch (fieldType) {
    case FieldType.Action:
    case FieldType.Label:
    case FieldType.Separator:
    case FieldType.FixedAttachment:
    case FieldType.FixedImage:
      return '';

    case FieldType.Draw:
    case FieldType.Photo:
    case FieldType.Signature:
    case FieldType.Attachment: {
      const count = Array.isArray(value) ? (value as AttachmentItemData[]).length : 0;
      if (!count) return '';
      return `${normalizedLabel} : ${count}`;
    }

    case FieldType.Notification: {
      const notificationSummary = getNotificationLabel(value, t);
      if (!notificationSummary) return '';
      return `${normalizedLabel} : ${notificationSummary}`;
    }

    case FieldType.Geolocation: {
      const geolocation = value as GeolocationData;
      if (geolocation?.longitude === undefined || geolocation?.latitude === undefined) return '';
      return `${normalizedLabel} : Lat: ${geolocation.latitude} / Lon: ${geolocation.longitude}`;
    }

    case FieldType.Time: {
      const formattedTime = formatDateValue(value, 'HH:mm');
      if (!formattedTime) return '';
      return `${normalizedLabel} : ${formattedTime}`;
    }

    case FieldType.DateTime: {
      const datePattern = type === 'DateTime' ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy';
      const formattedDateTime = formatDateValue(value, datePattern);
      if (!formattedDateTime) return '';
      return `${normalizedLabel} : ${formattedDateTime}`;
    }

    case FieldType.Combo:
    case FieldType.RadioList: {
      const choiceLabel = (value as Choice | undefined)?.label?.trim() ?? '';
      if (!choiceLabel) return '';
      return `${normalizedLabel} : ${choiceLabel}`;
    }

    case FieldType.CheckBoxList:
    case FieldType.HierarchicalList: {
      const listLabel = getChoiceListLabel(value);
      if (!listLabel) return '';
      return `${normalizedLabel} : ${listLabel}`;
    }

    default:
      return `${normalizedLabel} : ${value}`;
  }
};

export default getSummaryLabel;
